/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, Input, OnInit, inject} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {PassportService} from '../../../../services/passport.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastService, ToastType} from '@siemens/ix-angular';
import {HttpError} from '../../../../models/http-error.interface';
import {PassportDto} from '../../../../models/Passport';

const HTTP_NOT_FOUND = 404;

@Component({
  selector: 'app-search-battery-pass',
  templateUrl: './search-battery-pass.component.html',
  styleUrl: './search-battery-pass.component.scss',
  standalone: false,
})
export class SearchBatteryPassComponent {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly passportService = inject(PassportService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  @Input() public isPublic = false;
  loading = false;

  invalidUUID = false;

  async onKeyDown(event: KeyboardEvent) {
    const inputElement = event.target as HTMLInputElement;
    this.invalidUUID = false;
    const passId: string = inputElement.value.trim();
    if (event.key === 'Enter' && (await this.checkForBatteryPass(passId))) {
      if (this.isPublic) {
        setTimeout(() => {
          window.open(
            this.location.prepareExternalUrl(`/public/${passId}`),
            '_blank',
          );
        });
      } else {
        await this.router.navigate(['app/view-passport', passId]);
      }
    }
  }

  async checkForBatteryPass(passId: string) {
    this.loading = true;
    if (!this.isValidPassId(passId)) {
      this.invalidUUID = true;
      this.loading = false;
      return false;
    }
    let pass: PassportDto;
    try {
      if (this.isPublic) {
        pass = await this.passportService.loadOnePublic(passId);
      } else {
        pass = await this.loadPrivateWithPublicFallback(passId);
      }
    } catch (error) {
      if ((error as HttpError).status === HTTP_NOT_FOUND) {
        return this.showError('overview.search.pass_not_found', passId, 'info');
      } else {
        return this.showError('overview.search.general_error', passId, 'error');
      }
    }
    if (passId.startsWith('CX') || pass.id === passId) {
      this.loading = false;
      return true;
    } else {
      return this.showError('overview.search.pass_not_found', passId);
    }
  }

  async loadPrivateWithPublicFallback(passId: string): Promise<PassportDto> {
    let pass: PassportDto;
    try {
      pass = await this.passportService.loadOne(passId, true);
    } catch {
      pass = await this.passportService.loadOnePublic(passId);
    }
    return pass;
  }

  async showError(
    messageKey: string,
    passId: string,
    type: ToastType = 'error',
  ) {
    const message = this.translate.instant(messageKey, {id: passId});
    await this.toastService.show({message: message, type});
    this.loading = false;
    return false;
  }

  isValidPassId(uuidOrCxId: string): boolean {
    const uuidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    const ctRegex = /^CX:([0-9a-zA-Z-_]{8,36}):([0-9a-zA-Z-_]{8,36})$/;
    return uuidRegex.test(uuidOrCxId) || ctRegex.test(uuidOrCxId);
  }
}
